import { Link, useLocation } from '@tanstack/react-router';

import type { ReactNode } from 'react';

import { BrandMark } from '../../components/brand-mark';
import { systemClock, type Clock } from '../../lib/clock';
import { useSession } from '../../lib/session';

import { CommandPalette } from './command-palette';
import { labelForPath, navigationFor } from './navigation';
import { NotificationBell } from './notification-bell';
import { UserMenu } from './user-menu';

/**
 * The authenticated frame.
 *
 * A dark navy rail down the full height of the window, and the work on a light
 * blue-grey page beside it. The rail carries the product's identity — the mark,
 * the product name, and **the agency's** name underneath, never the vendor's —
 * then the sections. Whoever is signed in lives in the header.
 *
 * ## Why the rail is dark and the page is not
 *
 * Because they are different kinds of thing and the eye should not have to be
 * told twice. Navigation is furniture: it is in the same place all day and a
 * coordinator stops seeing it, which is what you want. The work is white cards
 * on a pale page, and a dark rail beside them means the boundary needs no
 * border, no shadow and no thinking about.
 *
 * ## The active item, said three ways
 *
 * A lifted navy fill, a 4px solid brand bar at the rail's edge, and
 * `aria-current="page"`. Colour is never the only signal — the fill and the bar
 * are two shapes, and the third is the one a screen reader gets. `app-shell.spec.tsx`
 * asserts the third, because it is the one nobody notices is missing.
 *
 * The bar used to carry the brand gradient, as did the mark and the primary
 * button. The gradient now has exactly one job — the accent across the top of a
 * measure card — and everything else is flat. Three things wearing the loudest
 * treatment in the system is three things a reader has to tell apart.
 */

/**
 * "Tuesday, August 4", under the section name.
 *
 * A scheduler's whole job is anchored to today, and the date is the one piece
 * of context that makes a schedule screen readable at a glance. Computed at
 * render rather than memoised — it is one `Intl` call, and a stale date on a
 * console someone leaves open overnight is worse than the work saved.
 *
 * The locale is the reader's, deliberately: `undefined` lets `Intl` use the
 * browser's. This is the one date in the product that is *not* a scheduling
 * datetime, so it carries none of §6.1's timezone rules — it is the day the
 * person is standing in.
 */
export function formatToday(clock: Clock): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(clock.now());
}

export function AppShell({
  children,
  clock = systemClock,
}: {
  children: ReactNode;
  clock?: Clock;
}): ReactNode {
  const permissions = useSession((state) => state.permissions);
  const agency = useSession((state) => state.agency);
  const sections = navigationFor(permissions);
  const location = useLocation();

  const section = labelForPath(location.pathname);

  return (
    <div className="bg-grad-page min-h-dvh md:flex">
      {/* Skip link: the rail is long, and a keyboard user should not have to
          walk it on every page (WCAG 2.4.1). */}
      <a
        href="#main"
        className="focus:bg-brand focus:text-ink-inv sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded-md focus:px-3 focus:py-2 focus:text-sm"
      >
        Skip to content
      </a>

      <nav
        aria-label="Sections"
        className="bg-grad-rail text-rail-ink w-rail flex flex-none flex-col md:sticky md:top-0 md:h-dvh"
      >
        {/* The same mark the sign-in card leads with — one component, so
            "exactly as it appears in the rail" is enforced rather than
            maintained. The subtitle is the agency, not a tagline and never the
            vendor's name: for someone who works at two agencies this is the
            most consequential fact on the screen, because every list they are
            about to read is scoped by it. */}
        <BrandMark
          on="rail"
          subtitle={agency?.displayName ?? 'Signing in…'}
          className="px-4 py-3"
        />

        {/* `pt-2` so the first section label — CARE — clears the agency name
            above it. Without it, "CARE" sat directly under "Riverside Home
            Care" and read as part of the agency's name rather than as the
            heading of the group beneath it. */}
        <div className="scroll-rail min-h-0 flex-1 overflow-y-auto px-3 pb-3 pt-2">
          {sections.map((navSection) => (
            <div key={navSection.label} className="mb-3 last:mb-0">
              <p className="text-rail-ink-muted text-2xs px-2 pb-1 font-semibold uppercase">
                {navSection.label}
              </p>

              <ul className="grid gap-0.5">
                {navSection.items.map((item) => {
                  const active =
                    location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
                  const Icon = item.icon;

                  return (
                    <li key={item.to} className="relative">
                      {active && (
                        <span
                          aria-hidden="true"
                          // At the rail's edge, not the pill's. `-left-3`
                          // cancels the list's own padding so the bar sits on
                          // the window edge the way the reference draws it.
                          // Solid, not the gradient it used to be. The gradient
                          // is the measure cards' accent and nothing else.
                          className="bg-brand absolute -left-3 bottom-1 top-1 w-1 rounded-r-sm"
                        />
                      )}
                      <Link
                        to={item.to}
                        aria-current={active ? 'page' : undefined}
                        className={`duration-fast ease-calm min-h-row-compact flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                          active
                            ? 'bg-rail-active text-rail-ink-strong font-semibold'
                            : 'text-rail-ink hover:bg-rail-hover hover:text-rail-ink-strong'
                        }`}
                      >
                        <Icon
                          aria-hidden="true"
                          className={`h-4 w-4 flex-none ${
                            active ? 'text-rail-ink-strong' : 'text-rail-ink-muted'
                          }`}
                        />
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="bg-surface border-line min-h-header sticky top-0 z-20 flex flex-wrap items-center justify-between gap-3 border-b px-6 py-2">
          <div className="min-w-0">
            {/* Not a heading. The page below owns the only `h1` on the screen,
                and a second one here would give every route two. */}
            <p className="text-ink truncate text-sm font-semibold">{section ?? 'Home Care'}</p>
            <p className="text-ink-3 truncate text-xs">{formatToday(clock)}</p>
          </div>

          <div className="flex flex-none items-center gap-2">
            <CommandPalette />
            <NotificationBell />
            {/* Top right, where the account lives in every other product a
                coordinator opens. Its menu drops down over the work rather than
                up over the navigation — see user-menu.tsx. */}
            <UserMenu />
          </div>
        </header>

        <main id="main" className="min-w-0 flex-1 px-4 py-6 md:px-6">
          {children}
        </main>
      </div>
    </div>
  );
}
